import { isEqual, pick } from 'lodash';
import { assign, createMachine, sendParent } from 'xstate';

import { Recipient, ValidateAddressResponse } from 'api/api';

import { ActorsSharedEvents } from './shared-types';
import { COUNTRIES_WITH_EXTENDED_REGION_VALIDATION } from './suggestion-service';

interface Context {
  validation: ValidateAddressResponse | null;
  address: Recipient | null;
  suggestionAddressSnapshot: Recipient | null;
  autocompleteSnapshot: Recipient | null;
  extendedValidation: boolean;
  country: string;
}

export const createSuggestionMachine = (
  country: string,
  extendedValidation: boolean,
  initialAddress?: Recipient
) =>
  createMachine(
    {
      predictableActionArguments: true,
      context: {
        // Validation is used externally by the component
        validation: null,
        address: initialAddress || null,
        suggestionAddressSnapshot: initialAddress || null,
        autocompleteSnapshot: initialAddress || null,
        extendedValidation,
        country,
      },
      tsTypes: {} as import('./suggestion-machine.typegen').Typegen0,
      schema: { context: {} as Context, events: {} as ActorsSharedEvents },
      id: 'suggestion',
      initial: initialAddress ? 'ready' : 'unverified',
      states: {
        unverified: {
          on: {
            AUTOCOMPLETE_CHANGE: {
              actions: ['updateAutocompleteSnapshot', 'updateAddressContext'],
              target: '#suggestion.ready.by_autocomplete',
            },
            SUGGESTION_UPDATE: [
              {
                actions: [
                  'updateSuggestionAddressSnapshot',
                  'updateValidationContext',
                  'sendReadyEvent',
                ],
                cond: 'suggestionIsValid',
                target: '#suggestion.ready.by_suggestion',
              },
              {
                actions: [
                  'updateSuggestionAddressSnapshot',
                  'updateValidationContext',
                  'sendSkipEvent',
                ],
                target: 'needs_confirmation',
              },
            ],
            ADDRESS_CHANGED: {
              actions: 'updateAddressContext',
            },
          },
        },
        needs_confirmation: {
          on: {
            ADDRESS_CHANGED: [
              {
                actions: ['updateAddressContext', 'resetSuggestion'],
                cond: 'essentialsDifferFromSuggestionSnapshot',
                target: 'unverified',
              },
              {
                actions: ['updateAddressContext', 'resetSuggestion'],
              },
            ],
            AUTOCOMPLETE_CHANGE: {
              actions: ['updateAutocompleteSnapshot', 'updateAddressContext'],
              target: '#suggestion.ready.by_autocomplete',
            },
          },
        },
        ready: {
          initial: 'by_autocomplete',
          states: {
            by_autocomplete: {
              on: {
                ADDRESS_CHANGED: {
                  cond: 'essentialsDifferFromAutocompleteSnapshot',
                  target: '#suggestion.unverified',
                },
                AUTOCOMPLETE_CHANGE: {
                  actions: ['updateAutocompleteSnapshot', 'updateAddressContext'],
                },
              },
            },
            by_suggestion: {},
          },
        },
      },
    },
    {
      actions: {
        updateSuggestionAddressSnapshot: assign({
          suggestionAddressSnapshot: context => context.address,
        }),
        updateAutocompleteSnapshot: assign({
          autocompleteSnapshot: (_, event) => event.payload.address,
        }),
        updateValidationContext: assign({
          validation: (_, event) => event.payload,
        }),
        updateAddressContext: assign({
          address: (_, event) => event.payload.address,
        }),
        sendSkipEvent: sendParent({ type: 'SKIP' }),
        sendReadyEvent: sendParent({ type: 'SUGGESTION_READY' }),
        resetSuggestion: assign({
          validation: null,
        }),
      },
      guards: {
        suggestionIsValid: (_, event) => event.payload.result === 'VALID',
        essentialsDifferFromAutocompleteSnapshot: (context, event) => {
          const essentialSnapshotFields = pick(context.autocompleteSnapshot, [
            'street',
            'street_number',
            'postal_code',
            'city',
            'address_lines',
            ...(extendedValidation &&
            COUNTRIES_WITH_EXTENDED_REGION_VALIDATION.includes(context.country)
              ? ['region']
              : []),
          ]);

          const essentialChangedFields = pick(event.payload.address, [
            'street',
            'street_number',
            'postal_code',
            'city',
            'address_lines',
            ...(extendedValidation &&
            COUNTRIES_WITH_EXTENDED_REGION_VALIDATION.includes(context.country)
              ? ['region']
              : []),
          ]);

          // Our backend strips spaces away, while our input mask and autocomplete keeps them
          // Feels sketchy, but it's the safest place to do this comparison
          essentialChangedFields.postal_code = essentialChangedFields.postal_code?.replace(' ', '');
          essentialSnapshotFields.postal_code = essentialSnapshotFields.postal_code?.replace(
            ' ',
            ''
          );

          if (isEqual(essentialSnapshotFields, essentialChangedFields)) {
            return false;
          }
          return true;
        },
        essentialsDifferFromSuggestionSnapshot: (context, event) => {
          const essentialSnapshotFields = pick(context.suggestionAddressSnapshot, [
            'street',
            'street_number',
            'postal_code',
            'city',
            'address_lines',
            ...(extendedValidation &&
            COUNTRIES_WITH_EXTENDED_REGION_VALIDATION.includes(context.country)
              ? ['region']
              : []),
          ]);

          const essentialChangedFields = pick(event.payload.address, [
            'street',
            'street_number',
            'postal_code',
            'city',
            'address_lines',
            ...(extendedValidation &&
            COUNTRIES_WITH_EXTENDED_REGION_VALIDATION.includes(context.country)
              ? ['region']
              : []),
          ]);

          if (isEqual(essentialSnapshotFields, essentialChangedFields)) {
            return false;
          }
          return true;
        },
      },
    }
  );

export type SuggestionMachine = ReturnType<typeof createSuggestionMachine>;
